import {z} from "zod"

/**
 * Asset categories used for identifying and browsing assets.
 *
 */
export const assetTypeSchema = z.enum(["Fabric", "Quilt", "Piping", "Zipper", "Handle", "Tape", "Puller"])
export type AssetType = z.infer<typeof assetTypeSchema>

export type LayerData = {
    name: string
    parameterIds: string[]
    stackId?: string
    layerId?: number
    canRemove: boolean
    canAdd: boolean
}

export type SideForHandle = "head" | "foot" | "left" | "right"
export type HandleCounts = 0 | 1 | 2
export type HandleCountsPerSide = Record<SideForHandle, HandleCounts>

export type HandleSettings = HandleCountsPerSide & {
    reference?: Handle
}

export type Accessory = {
    type: string
    inputParameterIds: string[]
}

export type AccessoryGroupData = {
    seamName: string
    seamId: string // common prefix for all descriptors belonging to this seam, e.g. "c8c0686b-.../mattress/accessories-bottom-curve"
    accessories: Accessory[]
}

export type UsedElements = {
    assets: {type: AssetType; colormassId?: string; bdId?: string}[]
    colors: string[]
    errors: string[]
}

/**
 * API used by the bd cloud editor.
 *
 * This api is provided inside a web component. Most of the functions are not relevant to the outside, see comments below.
 */
export interface BdApi {
    setMaterial(inputId: string, materialArticleId: string): Promise<void>
    setQuilt(inputId: string, quiltId: string | undefined): Promise<void>
    setQuiltStrength(inputId: string, strength: number): void
    setMaterialHorizontalOffset(inputId: string, offset: number): void
    setMaterialVerticalOffset(inputId: string, offset: number): void
    setMaterialRotation(inputId: string, rotation: number): void
    setQuiltHorizontalOffset(inputId: string, offset: number): void
    setQuiltVerticalOffset(inputId: string, offset: number): void
    setColor(inputId: string, color: string): void
    setColorWithSubstrate(inputId: string, color: string, substrateId: number): void
    setNumberInput(id: string, value: number): void
    removeAccessory(seamId: string, displayIndex: number): void
    assignTemplateToAccessory(templateInputId: string, templateId: string): void
    removePuller(templateInputId: string): void
    addAccessory(seamId: string, templateId: string): Promise<void>
    moveAccessory(seamId: string, sourceDisplayIndex: number, targetDisplayIndex: number): void
    addLayer(insertAtIndex: number): void
    removeLayer(layerIndex: number): void
    moveLayer(sourceIndex: number, targetIndex: number): void
    canMoveLayer(sourceIndex: number, targetIndex: number): boolean
    setLayerHeight(layerId: string, height: number): void
    setHandleCount(side: SideForHandle, count: HandleCounts): void
    setHandle(handleId: string): Promise<void>
    removeHandle(): void
    setHandleMaterial(inputId: string, materialArticleId: string): Promise<void>
    setHandleColorWithSubstrate(inputId: string, color: string, substrateId: number): void
    setHandleMaterialHorizontalOffset(inputId: string, offset: number): void
    setHandleMaterialVerticalOffset(inputId: string, offset: number): void
    setHandleMaterialRotation(inputId: string, rotation: number): void
    persistCurrentCameraView(): void
    clearSelection(): void

    //The following functions are used outside of the editor component

    /**
     * Registers a callback function that will be invoked whenever the configurator state changes.
     *
     * @param callback - A function to be called on configurator state changes.
     * @returns A function that can be called to unregister the callback.
     */
    registerStateChangeCallback(callback: (configuratorState: ConfiguratorState) => void): () => void
    sync(waitForOptionalTasks?: boolean): Promise<void>
    undo(): void
    redo(): void
    canUndo(): boolean
    canRedo(): boolean
    getDataForSaving(): TemplateDataForSaving
    getUsedElements(): Promise<UsedElements>
    getCurrentCameraParams(): string
    setCamera(params: string): void
    loadDefaultCamera(): Promise<void>
}

/**
 * Web component that provides the custom api used by the bd cloud editor.
 *
 * Used as <cm-bdcloud-api></cm-bdcloud-api>.
 */
export interface BdApiWebcomponent extends HTMLElement {
    bdApi: BdApi
}

/**
 * Data structure for saving a template revision.
 *
 * On BD cloud, configurations are created by copying templates. Each template has a revision, which is the only relevant part
 * for saving. When loading a template in the editor, the latest revision is automatically loaded. BD cloud models always
 * have only a single revision.
 *
 * @property templateRevisionId - UUID of the template revision.
 * @property serializedTemplateGraph - Serialized representation of the template graph that includes all changes made. The internals are not relevant at this point - this is passed to the backend for saving.
 */
export type TemplateDataForSaving = {
    templateRevisionId: string
    serializedTemplateGraph: unknown
}

/*The following material assignments exist:
- Fabric + optional quilt
- Color with substrate (pipings and tapes)
- Color only (for zippers, the substrate is defined in the zipper template).

- Material transform only applies to fabric/quilt*/
export type MaterialTransform = {
    horizontalOffset: number
    verticalOffset: number
    rotation: number
}

export type Material = {
    materialArticleId: string | undefined
    materialId: string | undefined
    horizontalOffset: number
    verticalOffset: number
    rotation: number
}

export type QuiltTransform = {
    quiltHorizontalOffset: number
    quiltVerticalOffset: number
}

export type QuiltedMaterial = Material &
    QuiltTransform & {
        quiltId: string | undefined
        quiltStrength: number
    }

export type Color = {
    color: string | undefined
}

export type ColorWithSubstrate = Color & {
    colorSubstrateLegacyId: number | undefined
}

export type MaterialOrColor = Material | QuiltedMaterial | ColorWithSubstrate | Color

export type NumberValue = {
    inputId: string
    name: string
    value: number
    min: number
    max: number
}

export function isMaterial(value: MaterialOrColor): value is Material {
    return "materialArticleId" in value && !("quiltId" in value)
}

export function isQuiltedMaterial(value: MaterialOrColor): value is QuiltedMaterial {
    return "materialArticleId" in value && "quiltId" in value
}

export function isColor(value: MaterialOrColor): value is Color {
    return !("materialArticleId" in value) && !("colorSubstrateLegacyId" in value)
}

export function isColorWithSubstrate(value: MaterialOrColor): value is ColorWithSubstrate {
    return !("materialArticleId" in value) && "colorSubstrateLegacyId" in value
}

export const PIPING_SUBSTRATE_MATTE_ID = 35478
export const PIPING_SUBSTRATE_GLOSSY_ID = 35517
export const PULLER_SUBSTRATE_ID = 43372 // 74e3e567-9bb2-451f-8d89-f669881cc8e4

export type FabricCapability = {
    allowQuilts: boolean
}

export type ColorCapability = {
    substrates: {id: number; name: string}[]
}

export type MaterialInput = {
    id: string
    name: string
    value: MaterialOrColor | undefined
} & ({fabric: FabricCapability; color?: ColorCapability} | {fabric?: FabricCapability; color: ColorCapability})

export function fabricInput(id: string, name: string, value: Material | QuiltedMaterial | undefined, opts: {allowQuilts: boolean}): MaterialInput {
    return {id, name, value, fabric: {allowQuilts: opts.allowQuilts}}
}

export function colorInput(
    id: string,
    name: string,
    value: Color | ColorWithSubstrate | undefined,
    opts?: {substrates?: {id: number; name: string}[]},
): MaterialInput {
    return {id, name, value, color: {substrates: opts?.substrates ?? []}}
}

export function fabricOrColorInput(
    id: string,
    name: string,
    value: MaterialOrColor | undefined,
    opts: {allowQuilts: boolean; substrates?: {id: number; name: string}[]},
): MaterialInput {
    return {
        id,
        name,
        value,
        fabric: {allowQuilts: opts.allowQuilts},
        color: {substrates: opts.substrates ?? []},
    }
}

export function pipingMaterialInput(id: string, name: string, value: MaterialOrColor | undefined) {
    return fabricOrColorInput(id, name, value, {
        allowQuilts: false,
        substrates: [
            {id: PIPING_SUBSTRATE_MATTE_ID, name: "Matte"},
            {id: PIPING_SUBSTRATE_GLOSSY_ID, name: "Glossy"},
        ],
    })
}

//Selectable elements
export type PipingOrTape = {
    type: "Piping" | "Tape"
    materialInput: MaterialInput
    templateInputId: string | undefined
    templateId: string | undefined
}

export type Zipper = {
    type: "Zipper"
    materialInput: MaterialInput
    templateInputId: string
    templateId: string
    puller0TemplateInputId: string
    puller: Puller | undefined
}

export type FabricLayer = {
    type: "FabricLayer"
    name: string
    materialInput: MaterialInput
    heightInputId?: string
    height?: number
}

export type Puller = {
    type: "Puller"
    materialInputs: MaterialInput[]
    positionInputId: string
    position: number
    templateInputId: string
    templateId: string
    /** Template id of the zipper this puller belongs to. Used to derive the puller's size filter (a puller always shares its zipper's size). */
    zipperTemplateId: string
}

export type Handle = {
    type: "Handle"
    materialInputs: MaterialInput[]
    templateInputId: string
    templateId: string
}

export type MeshWithInputs = {
    type: "MeshWithInputs"
    instanceId: string
    elementType: string
    meshName: string
    materialInputs: MaterialInput[]
}

export function getSelectionKey(element: SelectableElement | undefined): string | undefined {
    if (!element) return undefined
    switch (element.type) {
        case "Piping":
        case "Tape":
            return `${element.type}`
        case "Zipper":
            return `Zipper`
        case "FabricLayer":
            return `FabricLayer`
        case "Puller":
            return `Puller`
        case "Handle":
            return `Handle`
        case "MeshWithInputs":
            return `MeshWithInputs`
    }
}
export type SelectableElement = PipingOrTape | Zipper | FabricLayer | Puller | Handle | MeshWithInputs

export type ModelType = "Mattress" | "Pillow"

export type ConfiguratorState = {
    dimensions?: NumberValue[]
    layers?: LayerData[]
    supportsHandles: boolean
    handleSettings?: HandleSettings
    accessoryGroupData: AccessoryGroupData[]
    selectedElement: SelectableElement | undefined
}
